import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { shallowEqual, useSelector } from 'react-redux';
import forge from 'apis/forge';
import eventManager from 'events/EventManager';
import Icon, { IconType } from 'components/Icon';
import ProfileIcon from 'components/Panel/RightPanel/ProfileIcon';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { CommentFrameEntry, Frame } from 'dragonmouse-shared/interfaces';
import { selectFrameCommentAuthor } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { formatRelativeTime } from 'utils';
import { AnchorPoint } from 'hooks/useAnchored';
import MentionCard from '../MentionCard';
import { MentionUser, computeMentionRanges, useMentionUsers } from '../mentions';
import * as S from './styles';

interface CommentProps {
	comment: CommentFrameEntry;
	frame: Frame;
	isReply?: boolean;
	onStartReply?: () => void;
}

const Comment = ({ comment, frame, isReply, onStartReply }: CommentProps) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editedMessage, setEditedMessage] = useState(comment.message);
	const [hoveredMention, setHoveredMention] = useState<{
		mentionUser: MentionUser;
		anchor: AnchorPoint;
	} | null>(null);

	const mentionUsers = useMentionUsers();

	// Only ids recorded on the entry at post time render as mentions —
	// hand-typed names never highlight, and ids of users who since left the
	// scene quietly resolve to plain text.
	const recordedMentionUsers = useMemo(() => {
		const ids = comment.mentionedUserIds;
		if (!ids?.length) return [];
		return mentionUsers.filter((mentionUser) => ids.includes(mentionUser.id));
	}, [mentionUsers, comment.mentionedUserIds]);

	const { user, authUser, inspectorRightPanelWidth } = useSelector(
		(r: RootReducer) => ({
			user: selectFrameCommentAuthor(r.db, comment.userId),
			authUser: r.mode.authUser,
			inspectorRightPanelWidth: r.mode.inspectorRightPanelWidth,
		}),
		shallowEqual
	);

	const displayName = user?.displayName || 'Unknown user';
	const isOwn = authUser?.id === comment.userId;
	const isResolved = !!comment.resolvedAt;
	const avatarSize = isReply ? 20 : 24;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onStartEdit = () => {
		setEditedMessage(comment.message);
		setIsEditing(true);
	};

	const onCancelEdit = () => {
		setIsEditing(false);
	};

	const onSaveEdit = () => {
		const trimmed = editedMessage.trim();
		if (!trimmed || trimmed === comment.message) {
			setIsEditing(false);
			return;
		}
		forge.updateFrameEntry({
			frameId: frame.id,
			entry: { ...comment, message: trimmed },
		});
		setIsEditing(false);
	};

	const onToggleResolved = () => {
		forge.updateFrameEntry({
			frameId: frame.id,
			entry: { ...comment, resolvedAt: isResolved ? null : new Date() },
		});
	};

	const onDelete = () => {
		forge.deleteFrameEntry({ frameId: frame.id, entryId: comment.id });
	};

	const onElementChipClick = () => {
		if (comment.elementId) {
			eventManager.emit('devigner-select-element', comment.elementId);
		}
	};

	// Mentions render as spans injected into the markdown tree, so hover is
	// handled by delegation on the content container rather than per-span.
	const onContentMouseOver = (e: React.MouseEvent) => {
		const el = (e.target as HTMLElement).closest?.('[data-mention-id]') as HTMLElement | null;
		if (!el) return;
		const mentionUser = recordedMentionUsers.find((u) => u.id === el.dataset.mentionId);
		if (!mentionUser) return;
		const rect = el.getBoundingClientRect();
		setHoveredMention({
			mentionUser,
			anchor: { left: rect.left, top: rect.top, bottom: rect.bottom },
		});
	};

	const onContentMouseOut = (e: React.MouseEvent) => {
		if ((e.target as HTMLElement).closest?.('[data-mention-id]')) {
			setHoveredMention(null);
		}
	};

	const onEditKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSaveEdit();
		} else if (e.key === 'Escape') {
			e.preventDefault();
			onCancelEdit();
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	// Rewrites text nodes in the markdown tree so '@Display Name' occurrences
	// become styled spans. Purely presentational — the message itself stays
	// plain text.
	const rehypeMentions = useMemo(() => {
		const splitText = (value: string) => {
			const ranges = computeMentionRanges(value, recordedMentionUsers);
			if (ranges.length === 0) return null;
			const parts: any[] = [];
			let pos = 0;
			for (const range of ranges) {
				if (range.start > pos) parts.push({ type: 'text', value: value.slice(pos, range.start) });
				parts.push({
					type: 'element',
					tagName: 'span',
					properties: {
						className: ['mention'],
						dataMentionId: range.mentionUser.id,
						dataSelf: range.mentionUser.isSelf ? 'true' : undefined,
					},
					children: [{ type: 'text', value: value.slice(range.start, range.end) }],
				});
				pos = range.end;
			}
			if (pos < value.length) parts.push({ type: 'text', value: value.slice(pos) });
			return parts;
		};
		return () => (tree: any) => {
			const walk = (node: any) => {
				if (!node.children) return;
				const next: any[] = [];
				for (const child of node.children) {
					if (child.type === 'text') {
						next.push(...(splitText(child.value) ?? [child]));
						continue;
					}
					if (child.tagName !== 'code' && child.tagName !== 'pre') walk(child);
					next.push(child);
				}
				node.children = next;
			};
			walk(tree);
		};
	}, [recordedMentionUsers]);

	return (
		<S.Comment $resolved={isResolved} $isReply={isReply}>
			<S.CommentHeader>
				<S.UserInfo>
					<ProfileIcon userId={comment.userId} size={avatarSize} />
					<S.UserId>{displayName}</S.UserId>
					{comment.elementId && !isReply && (
						<S.ElementChip onClick={onElementChipClick} title="Jump to element">
							<Icon type={IconType.SELECT} width={9} height={9} />
							element
						</S.ElementChip>
					)}
					{isResolved && <S.ResolvedBadge>Resolved</S.ResolvedBadge>}
				</S.UserInfo>
				{inspectorRightPanelWidth > 250 && (
					<S.Timestamp>{formatRelativeTime(new Date(comment.createdAt))}</S.Timestamp>
				)}
			</S.CommentHeader>
			<S.CommentContent onMouseOver={onContentMouseOver} onMouseOut={onContentMouseOut}>
				{isEditing ? (
					<MultilineInput
						value={editedMessage}
						onInput={(e) =>
							setEditedMessage((e.target as HTMLTextAreaElement).value)
						}
						onKeyDown={onEditKeyDown}
						minLines={2}
						renderBorder={true}
					/>
				) : (
					<ReactMarkdown rehypePlugins={[rehypeMentions]}>{comment.message}</ReactMarkdown>
				)}
			</S.CommentContent>
			{hoveredMention && (
				<MentionCard mentionUser={hoveredMention.mentionUser} anchor={hoveredMention.anchor} />
			)}
			{isEditing ? (
				<S.EditActions>
					<S.EditButton onClick={onCancelEdit}>Cancel</S.EditButton>
					<S.EditButton onClick={onSaveEdit}>Save</S.EditButton>
				</S.EditActions>
			) : (
				<S.Actions data-comment-actions>
					{onStartReply && (
						<S.ActionButton onClick={onStartReply} title="Reply">
							<Icon type={IconType.CHAT} width={12} height={12} />
						</S.ActionButton>
					)}
					{isOwn && (
						<>
							<S.ActionButton
								onClick={onToggleResolved}
								title={isResolved ? 'Unresolve' : 'Resolve'}
							>
								<Icon type={IconType.CHECK} width={12} height={12} />
							</S.ActionButton>
							<S.ActionButton onClick={onStartEdit} title="Edit">
								<Icon type={IconType.EDIT} width={12} height={12} />
							</S.ActionButton>
							<S.ActionButton onClick={onDelete} title="Delete">
								<Icon type={IconType.TRASH} width={12} height={12} />
							</S.ActionButton>
						</>
					)}
				</S.Actions>
			)}
		</S.Comment>
	);
};

export default Comment;
